import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { formatChatTRY } from "../utils/money";
import { sfx } from "../utils/sfx";

/** Winner slot — must stay inside strip length below. */
const WIN_INDEX = 52;
const STRIP_LEN = WIN_INDEX + 8;
const SPIN_MS = 5000;
const AUTO_CLOSE_MS = 8000;

/** Long fast spin, short settle at the end. */
function spinEase(t) {
  const fastEnd = 0.78;
  const atFast = 0.86;
  if (t <= fastEnd) {
    return (t / fastEnd) ** 0.92 * atFast;
  }
  const tail = (t - fastEnd) / (1 - fastEnd);
  return atFast + (1 - (1 - tail) ** 3.5) * (1 - atFast);
}

const tierOf = (amount) => {
  if (!(amount > 0)) return "empty";
  if (amount >= 200) return "gold";
  if (amount >= 50) return "purple";
  return "blue";
};

const revealMeta = (amount) => {
  const n = Number(amount) || 0;
  if (!(n > 0)) {
    return { tier: "empty", headline: "Boş", sub: "Bu sefer olmadı", win: false };
  }
  if (n >= 200) {
    return { tier: "gold", headline: formatChatTRY(n), sub: "Jackpot!", win: true };
  }
  if (n >= 50) {
    return { tier: "purple", headline: formatChatTRY(n), sub: "Süper ödül!", win: true };
  }
  return { tier: "blue", headline: formatChatTRY(n), sub: "Kazandın!", win: true };
};

const labelOf = (amount) => {
  if (!(amount > 0)) return "Boş";
  return formatChatTRY(amount);
};

const decoyAmount = (winAmount) => {
  const roll = Math.random();
  if (roll < 0.18) return 0;
  if (roll < 0.62) return Math.round(5 + Math.random() * 45);
  if (roll < 0.88) return Math.round(50 + Math.random() * 150);
  const high = Math.max(180, Number(winAmount) * 0.6);
  return Math.round(high + Math.random() * 280);
};

const buildStrip = (winAmount) => {
  const items = [];
  for (let i = 0; i < STRIP_LEN; i += 1) {
    if (i === WIN_INDEX) {
      items.push({ amount: winAmount, win: true });
    } else {
      items.push({ amount: decoyAmount(winAmount), win: false });
    }
  }
  return items;
};

const measureStride = (trackEl) => {
  if (!trackEl?.children?.length) return 100;
  const first = trackEl.children[0];
  const second = trackEl.children[1];
  if (second) return second.offsetLeft - first.offsetLeft;
  return first.offsetWidth + 8;
};

const ChatCaseRoll = ({ amount, onDone }) => {
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const doneRef = useRef(false);
  const onDoneRef = useRef(onDone);
  const tickRef = useRef(null);
  const landedRef = useRef(false);
  const spinRef = useRef({ startOffset: 0, endOffset: 0 });
  const [offset, setOffset] = useState(0);
  const [landed, setLanded] = useState(false);
  const [showReveal, setShowReveal] = useState(false);
  const winAmount = Number(amount) || 0;
  const meta = useMemo(() => revealMeta(winAmount), [winAmount]);
  const items = useMemo(() => buildStrip(winAmount), [winAmount]);

  const close = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    onDoneRef.current?.();
  };

  useEffect(() => {
    onDoneRef.current = onDone;
  }, [onDone]);

  useEffect(() => {
    document.body.classList.add("gc-roll-lock");
    return () => document.body.classList.remove("gc-roll-lock");
  }, []);

  useLayoutEffect(() => {
    doneRef.current = false;
    landedRef.current = false;
    setLanded(false);
    setShowReveal(false);

    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track) return undefined;

    const stride = measureStride(track);
    const vw = viewport.clientWidth;
    const winCenter = WIN_INDEX * stride + stride / 2;
    const endOffset = Math.max(0, winCenter - vw / 2);
    const startOffset = 0;
    spinRef.current = { startOffset, endOffset };

    setOffset(startOffset);
    sfx.caseSpinTick();
    tickRef.current = window.setInterval(() => sfx.caseSpinTick(), 88);

    let frame = 0;
    const startTs = performance.now();

    const land = () => {
      if (landedRef.current) return;
      landedRef.current = true;
      window.clearInterval(tickRef.current);
      setOffset(endOffset);
      setLanded(true);
      window.setTimeout(() => {
        sfx.caseReveal(winAmount);
        setShowReveal(true);
      }, 280);
      window.setTimeout(close, AUTO_CLOSE_MS);
    };

    const tick = (ts) => {
      const elapsed = ts - startTs;
      const p = Math.min(1, elapsed / SPIN_MS);
      const { startOffset: from, endOffset: to } = spinRef.current;
      setOffset(from + (to - from) * spinEase(p));
      if (p < 1) {
        frame = requestAnimationFrame(tick);
      } else {
        land();
      }
    };

    frame = requestAnimationFrame(tick);
    const hardStop = window.setTimeout(land, SPIN_MS + 500);

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(hardStop);
      window.clearInterval(tickRef.current);
    };
  }, [winAmount]);

  const ui = (
    <div className="gc-roll-overlay is-fullscreen" role="dialog" aria-modal="true" aria-label="Kasa açılıyor">
      <div className={`gc-roll-shell${showReveal ? " has-reveal" : ""}`}>
        <p className="gc-roll-title">{showReveal ? "Sonuç" : landed ? "Durdu" : "Kasa dönüyor"}</p>

        <div className="gc-roll-pointer" aria-hidden>
          <span className="gc-roll-arrow" />
          <span className="gc-roll-arrow-label">Ödül burada</span>
        </div>

        <div className={`gc-roll-viewport is-fullscreen-vp${landed ? " is-landed" : ""}`} ref={viewportRef}>
          <div className="gc-roll-marker" aria-hidden />
          <div className="gc-roll-track" ref={trackRef} style={{ transform: `translate3d(${-offset}px, 0, 0)` }}>
            {items.map((item, index) => (
              <div
                key={`${index}-${item.amount}-${item.win ? "w" : "d"}`}
                className={`gc-roll-item tier-${tierOf(item.amount)}${item.win ? " is-win" : ""}${landed && item.win ? " landed" : ""}`}
              >
                <span className="gc-roll-item-label">{labelOf(item.amount)}</span>
                <i className="gc-roll-tier-bar" />
              </div>
            ))}
          </div>
        </div>

        {showReveal ? (
          <div className={`gc-roll-reveal is-fullscreen-reveal tier-${meta.tier}${meta.win ? " is-win" : " is-miss"}`}>
            {meta.win ? <div className="gc-roll-burst" aria-hidden /> : null}
            <p className="gc-roll-reveal-sub">{meta.sub}</p>
            <p className="gc-roll-reveal-amount">{meta.headline}</p>
            <p className="gc-roll-reveal-hint">{meta.win ? "Bakiyene eklendi" : "Şansını tekrar dene"}</p>
            <button type="button" className="gc-roll-reveal-btn" onClick={close}>
              Tamam
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );

  return createPortal(ui, document.body);
};

export default ChatCaseRoll;
